import React, { useState, useEffect } from "react";
import {
    PopupOverlay,
    PopupContainer,
    PopupTitle,
    PopupButton,
    TabContainer,
    TabHeader,
    TabButton,
    TabContent,
    TabLeft,
    TabRight,
    TabBody1,
    TabBody2,
    TabBody3,
    DateRangeContainer,
    DateInput,
    Arrow,
    Label,
    CustomDatePicker,
    StyledTextArea,
    ButtonWrap,
    ListingInfo,
    ListingInfoTitle,
    ListingInfoImg,
    ListingInfoWrap,
    ListingInfoTag,
    ListingInfoWrap2,
    HolidayNote,
    DatePickerGlobalStyle,
    Radio
} from "./PopUp.styles";
import Input from "../components/Input";
import "react-datepicker/dist/react-datepicker.css";

const PopUp = ({ data, onClose, onSave, isLoading = false, showToast }) => {

    const [activeTab, setActiveTab] = useState(0);
    const [selectedDays, setSelectedDays] = useState([]);
    const [newPrice, setNewPrice] = useState(data?.price ?? 0);
    const [weekdayPrices, setWeekdayPrices] = useState({});

    const isBlockEditMode = data?.mode === "block-edit";
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

    const [formData, setFormData] = useState({
        startDate: data?.startDate ? new Date(data.startDate) : null,
        endDate: data?.endDate ? new Date(data.endDate) : null,
        block: false,
        holidayName: "",
        noteForPartner: "",
        listing: data?.listing
            ? { ...data.listing }
            : {
                listingId: "",
                name: "",
                bedrooms: 0,
                bathrooms: 0,
                guests: 0,
                image: "/images/default.png",
            },
    });


    const weekdayNames = {
        0: "Sun",
        1: "Mon",
        2: "Tue",
        3: "Wed",
        4: "Thu",
        5: "Fri",
        6: "Sat",
    };

    const formatCurrencySymbol = (currency) => {
        switch (currency) {
            case "AUD":
                return "AUD";
            case "GBP":
                return "GBP";
            case "USD":
                return "USD";
            case "EUR":
                return "EUR";
            default:
                return currency || "AUD";
        }
    };

    const calculateWeekdayPrices = (startDate, endDate, listingId) => {
        if (!startDate || !endDate || !listingId) {
            setWeekdayPrices({});
            return;
        }

        // 🆕 優先使用傳入的 calendarData，否則使用 localStorage
        let calendarData = data.calendarData;
        console.log("🔍 PopUp 調試資訊:");
        console.log("📊 data.calendarData:", data.calendarData);
        console.log("📊 data 完整內容:", data);

        if (!calendarData) {
            console.log("沒有傳入 calendarData，嘗試從 localStorage 讀取");
            const cachedCalendar = localStorage.getItem("cachedCalendarData");
            if (!cachedCalendar) {
                console.log("❌ localStorage 也沒有快取資料");
                setWeekdayPrices({});
                return;
            }
            calendarData = JSON.parse(cachedCalendar);
            console.log("從 localStorage 讀取到資料:", Object.keys(calendarData).length, "個月份");
        } else {
            console.log("使用傳入的 calendarData:", Object.keys(calendarData).length, "個月份");
        }

        const weekdayStats = {
            0: { available: [], booked: [], blocked: [] },
            1: { available: [], booked: [], blocked: [] },
            2: { available: [], booked: [], blocked: [] },
            3: { available: [], booked: [], blocked: [] },
            4: { available: [], booked: [], blocked: [] },
            5: { available: [], booked: [], blocked: [] },
            6: { available: [], booked: [], blocked: [] },
        };

        const current = new Date(startDate);
        const end = new Date(endDate);

        while (current <= end) {
            const year = current.getFullYear();
            const month = String(current.getMonth() + 1).padStart(2, "0");
            const day = String(current.getDate()).padStart(2, "0");
            const key = `${listingId}-${year}-${month}`;
            const dateStr = `${year}-${month}-${day}`;

            const monthData = calendarData[key] || [];
            const dayData = monthData.find((d) => {
                const dDate = new Date(d.date);
                const y = dDate.getFullYear();
                const m = String(dDate.getMonth() + 1).padStart(2, "0");
                const dd = String(dDate.getDate()).padStart(2, "0");
                const dYmd = `${y}-${m}-${dd}`;
                return dYmd === dateStr;
            });

            const weekday = current.getDay();

            // ⭐ 優先使用 reservationMap 檢查是否 booked
            let isBooked = false;
            if (data.reservationMap && data.reservationMap[listingId]) {
                const reservations = data.reservationMap[listingId];
                isBooked = reservations.some(res => {
                    const checkIn = new Date(res.CheckIn);
                    const checkOut = new Date(checkIn);
                    checkOut.setDate(checkOut.getDate() + res.Nights);
                    return current >= checkIn && current < checkOut;
                });
            }

            if (dayData) {
                if (isBooked) {
                    // ⭐ 使用 reservationMap 判斷 booked
                    weekdayStats[weekday].booked.push({
                        date: dateStr,
                        price: dayData.price,
                        currency: dayData.currency
                    });
                } else if (dayData.status === "blocked") {
                    weekdayStats[weekday].blocked.push({
                        date: dateStr,
                        price: dayData.price,
                        currency: dayData.currency
                    });
                } else {
                    weekdayStats[weekday].available.push({
                        date: dateStr,
                        price: dayData.price,
                        currency: dayData.currency
                    });
                }
            }

            current.setDate(current.getDate() + 1);
        }

        const result = {};
        Object.keys(weekdayStats).forEach(weekday => {
            const stats = weekdayStats[weekday];
            const availablePrices = stats.available.map(d => d.price).filter(p => p != null);

            if (availablePrices.length > 0) {
                // 有可編輯的日期
                const minPrice = Math.min(...availablePrices);
                const maxPrice = Math.max(...availablePrices);
                const currency = stats.available[0].currency;

                result[weekday] = {
                    min: minPrice,
                    max: maxPrice,
                    currency: currency,
                    display: minPrice === maxPrice
                        ? `${minPrice}`
                        : `${minPrice} - ${maxPrice}`,
                    available: true,
                    availableCount: stats.available.length,
                    bookedCount: stats.booked.length,
                    blockedCount: stats.blocked.length,
                };
            } else if (stats.booked.length > 0 || stats.blocked.length > 0) {
                // 全部都是 booked/blocked
                result[weekday] = {
                    min: 0,
                    max: 0,
                    currency: "AUD",
                    display: "N/A",
                    available: false,
                    availableCount: 0,
                    bookedCount: stats.booked.length,
                    blockedCount: stats.blocked.length,
                };
            }
        });

        setWeekdayPrices(result);
    };

    useEffect(() => {
        const isEditPriceTab = (data?.hideBlockTab && activeTab === 0) || (!data?.hideBlockTab && activeTab === 1);
        if (isEditPriceTab && formData.startDate && formData.endDate) {
            const listingId = data.listingId ?? data.listing?.listingId;




            if (listingId) {
                calculateWeekdayPrices(formData.startDate, formData.endDate, listingId);
            }
        } else if (isEditPriceTab) {
            setWeekdayPrices({});
        }
    }, [formData.startDate, formData.endDate, activeTab, data.listingId, data.listing, data?.hideBlockTab, data.calendarData, data.reservationMap,]);

    const toggleDay = (day) => {
        setSelectedDays((prev) =>
            prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
        );
    };

    useEffect(() => {
        if (data?.listing) {
            setFormData((prev) => ({
                ...prev,
                listing: { ...data.listing },
            }));
        }
    }, [data?.listing]);

    useEffect(() => {
        if (data?.mode === "block-edit") {
            setActiveTab(0);
            setFormData((prev) => ({
                ...prev,
                startDate: new Date(data.startDate),
                endDate: new Date(data.endDate),
                listing: data.listing ?? prev.listing,
            }));
        }
    }, [data]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleDateChange = (field, value) => {
        setFormData((prev) => {
            const updated = { ...prev, [field]: value };
            if (updated.startDate && updated.endDate) {
                updated.block = true;
            }
            return updated;
        });
    };

    const handleBlockSave = () => {
        // 防止重複點擊
        if (isLoading) {
            console.log("正在處理中，請勿重複點擊");
            return;
        }

        if (!formData.startDate || !formData.endDate) {
            alert("Please select start & end date");
            return;
        }
        const listingId = data.listingId ?? data.listing?.listingId;

        // 🔥 檢查選取範圍內是否有 reservation（僅記錄，不阻止保存）
        let bookedDatesWarning = [];
        if (data.reservationMap && data.reservationMap[listingId]) {
            const reservations = data.reservationMap[listingId];
            const start = new Date(formData.startDate);
            const end = new Date(formData.endDate);
            const bookedDates = [];

            // 檢查每個 reservation 是否與選取的範圍衝突
            reservations.forEach(res => {
                const checkIn = new Date(res.CheckIn);
                const checkOut = new Date(checkIn);
                checkOut.setDate(checkOut.getDate() + res.Nights);

                // 檢查日期範圍是否重疊
                const current = new Date(Math.max(start, checkIn));
                const rangeEnd = new Date(Math.min(end, checkOut));

                while (current < rangeEnd) {
                    const year = current.getFullYear();
                    const month = String(current.getMonth() + 1).padStart(2, "0");
                    const day = String(current.getDate()).padStart(2, "0");
                    const dateStr = `${year}-${month}-${day}`;
                    bookedDates.push(dateStr);
                    current.setDate(current.getDate() + 1);
                }
            });
            // 如果有 booked 日期，記錄下來但不阻止保存
            if (bookedDates.length > 0) {
                bookedDatesWarning = bookedDates;
                console.log("⚠️ 範圍內有已預訂日期:", bookedDates);
            }

            if (bookedDates.length > 0) {
                alert(
                    `❌ Cannot block dates!\n\n` +
                    `The following dates are already booked:\n` +
                    `${bookedDates.map(d => `  • ${d}`).join('\n')}\n\n` +
                    `Please select a different date range that doesn't include booked dates.`
                );
                return;
            }
        }

        // 使用 calendarData 檢查 (如果沒有 reservationMap)
        const cachedCalendar = localStorage.getItem("cachedCalendarData");

        if (cachedCalendar && !data.reservationMap) {
            const calendarData = JSON.parse(cachedCalendar);
            const start = new Date(formData.startDate);
            const end = new Date(formData.endDate);
            const current = new Date(start);

            const bookedDates = [];

            while (current <= end) {
                const year = current.getFullYear();
                const month = String(current.getMonth() + 1).padStart(2, "0");
                const day = String(current.getDate()).padStart(2, "0");
                const key = `${listingId}-${year}-${month}`;
                const dateStr = `${year}-${month}-${day}`;

                const monthData = calendarData[key] || [];
                const dayData = monthData.find(d => {
                    const dDate = typeof d.date === 'string' ? d.date.split('T')[0] : d.date;
                    return dDate === dateStr;
                });

                const isDayBooked = dayData && dayData.status === "booked";

                if (isDayBooked) {
                    bookedDates.push(dateStr);
                }

                current.setDate(current.getDate() + 1);
            }

            if (bookedDates.length > 0) {
                alert(
                    `❌ Cannot block dates!\n\n` +
                    `The following dates are already booked:\n` +
                    `${bookedDates.map(d => `  • ${d}`).join('\n')}\n\n` +
                    `Please select a different date range that doesn't include booked dates.`
                );
                return;
            }
        }

        const toYMD = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
                d.getDate()
            ).padStart(2, "0")}`;



        if (data?.mode === "block-edit") {
            onSave({
                mode: "block-edit",
                listingId,
                startDate: toYMD(formData.startDate),
                endDate: toYMD(formData.endDate),
            });
        } else {
            onSave({
                mode: "block",
                listingId,
                startDate: toYMD(formData.startDate),
                endDate: toYMD(formData.endDate),
            });
        }
    };

    const handlePriceSave = () => {
        // 🆕 防止重複點擊
        if (isLoading) {
            console.log("正在處理中，請勿重複點擊");
            return;
        }

        if (!formData.startDate || !formData.endDate) {
            alert("Please select start & end date");
            return;
        }

        const listingId = data.listingId ?? data.listing?.listingId;

        // 檢查選取範圍內是否有 reservation（僅記錄，不阻止保存）
        let bookedDatesWarning = [];
        if (data.reservationMap && data.reservationMap[listingId]) {
            const reservations = data.reservationMap[listingId];
            const start = new Date(formData.startDate);
            const end = new Date(formData.endDate);
            const bookedDates = [];

            // 檢查每個 reservation 是否與選取的範圍衝突
            reservations.forEach(res => {
                const checkIn = new Date(res.CheckIn);
                const checkOut = new Date(checkIn);
                checkOut.setDate(checkOut.getDate() + res.Nights);

                // 檢查日期範圍是否重疊
                const current = new Date(Math.max(start, checkIn));
                const rangeEnd = new Date(Math.min(end, checkOut));

                while (current < rangeEnd) {
                    const year = current.getFullYear();
                    const month = String(current.getMonth() + 1).padStart(2, "0");
                    const day = String(current.getDate()).padStart(2, "0");
                    const dateStr = `${year}-${month}-${day}`;
                    bookedDates.push(dateStr);
                    current.setDate(current.getDate() + 1);
                }
            });

            // 如果有 booked 日期，記錄下來但不阻止保存
            if (bookedDates.length > 0) {
                bookedDatesWarning = bookedDates;
                console.log("⚠️ 範圍內有已預訂日期:", bookedDates);
            }
        }

        // 收集所有有輸入新價格的 weekday
        const updatedWeekdays = Object.keys(weekdayPrices)
            .filter(weekday => {
                const hasNewPrice = weekdayPrices[weekday]?.newPrice;
                const newPrice = weekdayPrices[weekday]?.newPrice;
                // 🆕 只收集有效的新價格（不為空且大於0）
                return hasNewPrice && newPrice !== "" && Number(newPrice) > 0;
            })
            .map(weekday => ({
                weekday: Number(weekday),
                newPrice: Number(weekdayPrices[weekday].newPrice)
            }));

        console.log("🔍 PopUp 詳細調試:");
        console.log("📊 原始 weekdayPrices:", weekdayPrices);
        console.log("🔍 過濾後的 updatedWeekdays:", updatedWeekdays);

        if (updatedWeekdays.length === 0) {
            if (showToast) {
                showToast("Please enter a valid price greater than 0.", true);
            }
            return;
        }

        const toYMD = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
                d.getDate()
            ).padStart(2, "0")}`;

        console.log("PopUp 調試資訊:");
        console.log("選取日期範圍:", toYMD(formData.startDate), "到", toYMD(formData.endDate));
        console.log("更新的星期價格:", updatedWeekdays);
        console.log("weekdayPrices 原始資料:", weekdayPrices);

        // 🔥 如果範圍內有 booked 日期，顯示提醒
        if (bookedDatesWarning.length > 0 && showToast) {
            const message = `⚠️ Note: The following dates are booked and will not be updated:\n${bookedDatesWarning.map(d => `• ${d}`).join('\n')}\n\nOther dates in the range will be updated.`;
            showToast(message, false);  // false = 綠色 Toast（不是錯誤）
        }

        // ✅ 呼叫 onSave（原本的邏輯）
        onSave({
            mode: "price",
            listingId,
            startDate: toYMD(formData.startDate),
            endDate: toYMD(formData.endDate),
            weekdayPrices: updatedWeekdays,
            bookedDates: bookedDatesWarning,  // 🔥 傳遞 booked 日期
        });

        // 🆕 步驟 1：清除該 listing 的所有 calendar cache
        const prefix = `calendar_${listingId}_`;
        const clearedKeys = [];

        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(prefix)) {
                localStorage.removeItem(key);
                clearedKeys.push(key);
            }
        });

        console.log(`🗑️ 已清除 ${clearedKeys.length} 個 calendar cache:`, clearedKeys);

        // 🆕 步驟 2：清除 cachedCalendarData（如果有的話）
        const cachedCalendarKey = "cachedCalendarData";
        if (localStorage.getItem(cachedCalendarKey)) {
            console.log(`🗑️ 清除 ${cachedCalendarKey}`);
            localStorage.removeItem(cachedCalendarKey);
        }

        // 🆕 步驟 3：發送更新事件通知其他頁面
        window.dispatchEvent(new CustomEvent('calendarPriceUpdated', {
            detail: {
                listingId: listingId,
                startDate: toYMD(formData.startDate),
                endDate: toYMD(formData.endDate),
                updatedWeekdays: updatedWeekdays,
                timestamp: Date.now()
            }
        }));

        console.log('📣 已發送價格更新事件通知');
    };

    const handleCancel = () => {
        onClose();
    };

    const handleTabClick = (index) => {
        if (isBlockEditMode && index === 1) {
            return;
        }

        setActiveTab(index);
    };


    const tabs = [
        {
            label: "Block Date",
            content: (
                <TabLeft>
                    <TabBody2>Dates</TabBody2>
                    <TabBody3>Choose a date range</TabBody3>

                    <DateRangeContainer>
                        <DateInput>
                            <CustomDatePicker
                                selected={formData.startDate}
                                onChange={(date) => handleDateChange("startDate", date)}
                                selectsStart
                                startDate={formData.startDate}
                                endDate={formData.endDate}
                                dateFormat="dd MMM yyyy"
                                placeholderText="Start Date"
                                minDate={new Date()}
                                withPortal={isMobile}
                                popperClassName="kozy-datepicker-popper"
                                popperPlacement="bottom-start"
                            />
                        </DateInput>
                        <Arrow>→</Arrow>
                        <DateInput>
                            <CustomDatePicker
                                selected={formData.endDate}
                                onChange={(date) => handleDateChange("endDate", date)}
                                selectsEnd
                                startDate={formData.startDate}
                                endDate={formData.endDate}
                                dateFormat="dd MMM yyyy"
                                placeholderText="End Date"
                                minDate={formData.startDate ?? new Date()}
                                withPortal={isMobile}
                                popperClassName="kozy-datepicker-popper"
                                popperPlacement="bottom-start"
                            />
                        </DateInput>
                    </DateRangeContainer>

                    <div style={{ marginTop: "20px" }}>
                        <TabBody1>Do you need cleaning services?</TabBody1>
                        <div style={{ display: "flex", gap: "20px", marginTop: "10px" }}>
                            <Label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <Radio
                                    checked={formData.block === true}
                                    onChange={() => handleInputChange("block", true)}
                                />
                                Yes
                            </Label>
                            <Label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <Radio
                                    checked={formData.block === false}
                                    onChange={() => handleInputChange("block", false)}
                                />
                                No
                            </Label>
                        </div>
                        <TabBody3 style={{ marginTop: "10px", opacity: 0.7 }}>
                            If you'd like us to take care of the cleaning for your property,
                            please note that a cleaning fee will be applied.
                        </TabBody3>
                    </div>

                    <div style={{ marginTop: "30px" }}>
                        <TabBody1>Note</TabBody1>
                        <StyledTextArea
                            placeholder="Enter your note..."
                            value={formData.noteForPartner}
                            onChange={(e) => handleInputChange("noteForPartner", e.target.value)}
                        />
                    </div>
                </TabLeft>
            )
        },
        {
            label: "Edit Price",
            content: (
                <TabLeft>
                    <TabBody2>Dates</TabBody2>
                    <TabBody3>Choose a date range</TabBody3>

                    <DateRangeContainer>
                        <DateInput>
                            <CustomDatePicker
                                selected={formData.startDate}
                                onChange={(date) => handleDateChange("startDate", date)}
                                selectsStart
                                startDate={formData.startDate}
                                endDate={formData.endDate}
                                dateFormat="dd MMM yyyy"
                                placeholderText="Start Date"
                                minDate={new Date()}
                                withPortal={isMobile}
                                popperClassName="kozy-datepicker-popper"
                                popperPlacement="bottom-start"
                            />
                        </DateInput>
                        <Arrow>→</Arrow>
                        <DateInput>
                            <CustomDatePicker
                                selected={formData.endDate}
                                onChange={(date) => handleDateChange("endDate", date)}
                                selectsEnd
                                startDate={formData.startDate}
                                endDate={formData.endDate}
                                dateFormat="dd MMM yyyy"
                                placeholderText="End Date"
                                minDate={formData.startDate ?? new Date()}
                                withPortal={isMobile}
                                popperClassName="kozy-datepicker-popper"
                                popperPlacement="bottom-start"
                            />
                        </DateInput>
                    </DateRangeContainer>

                    <div style={{ marginTop: "20px" }}>
                        <div style={{ display: "flex", gap: "20px", marginBottom: "12px" }}>
                            <TabBody1 style={{ flex: "0 0 60px" }}></TabBody1>
                            <TabBody1 style={{ flex: 1 }}>Original Price</TabBody1>
                            <TabBody1 style={{ flex: 1 }}>Edit Price</TabBody1>
                        </div>

                        {[1, 2, 3, 4, 5, 6, 0].map(weekday => {
                            const priceData = weekdayPrices[weekday];
                            const isDisabled = !priceData || priceData.availableCount === 0;
                            const weekdayKey = weekdayNames[weekday];

                            return (
                                <div
                                    key={weekday}
                                    style={{
                                        display: "flex",
                                        gap: "20px",
                                        alignItems: "center",
                                        marginBottom: "12px",
                                        opacity: isDisabled ? 0.5 : 1
                                    }}
                                >
                                    <div style={{ flex: "0 0 60px" }}>
                                        <TabBody2>{weekdayKey}</TabBody2>


                                        {/* 新增: 顯示統計資訊 */}
                                        {priceData && (
                                            <div style={{
                                                fontSize: "12px",
                                                color: isDisabled ? "#999" : "#666",
                                                marginTop: "2px"
                                            }}>
                                                {priceData.availableCount > 0 ? (
                                                    // 🔥 有 available 的日期
                                                    <div>
                                                        <span style={{ color: "#28a745" }}>
                                                            {priceData.availableCount} available
                                                        </span>
                                                        {priceData.bookedCount > 0 && (
                                                            <span style={{ color: "#dc3545", marginLeft: "8px" }}>
                                                                ({priceData.bookedCount} booked)
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : priceData.bookedCount > 0 ? (
                                                    // 🔥 全部都是 booked
                                                    <span style={{ color: "#dc3545", fontWeight: 600 }}>
                                                        All dates booked
                                                    </span>
                                                ) : priceData.blockedCount > 0 ? (
                                                    // 🔥 全部都是 blocked
                                                    <span style={{ color: "#6c757d", fontWeight: 600 }}>
                                                        All dates blocked
                                                    </span>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>

                                    <Input
                                        type="text"
                                        disabled
                                        value={isDisabled ? "" : `${priceData.currency} ${priceData.display}`}
                                        placeholder={isDisabled ? "Unavailable" : ""}
                                        style={{
                                            flex: 1,
                                            backgroundColor: "#f5f5f5",
                                            cursor: "not-allowed"
                                        }}
                                    />

                                    <Input
                                        type="number"
                                        disabled={isDisabled}
                                        placeholder={
                                            isDisabled
                                                ? "Unavailable"
                                                : `${priceData.currency} ${priceData.min}`
                                        }
                                        value={weekdayPrices[weekday]?.newPrice || ""}
                                        onChange={(e) => {
                                            setWeekdayPrices(prev => ({
                                                ...prev,
                                                [weekday]: {
                                                    ...prev[weekday],
                                                    newPrice: e.target.value
                                                }
                                            }));
                                        }}
                                        style={{
                                            flex: 1,
                                            cursor: isDisabled ? "not-allowed" : "pointer"
                                        }}
                                    />
                                </div>
                            );
                        })}

                    </div>

                </TabLeft >
            ),
        },
    ];
    const availableTabs = tabs.slice(1);

    if (!data) return null;



    return (
        <>
            <DatePickerGlobalStyle />
            <PopupOverlay>
                <PopupContainer onClick={(e) => e.stopPropagation()}>
                    <PopupTitle>
                        {availableTabs[activeTab]?.label || "Edit Price"}
                    </PopupTitle>

                    <TabContainer>
                        <TabHeader>
                            {availableTabs.map((tab, index) => (
                                <TabButton
                                    key={index}
                                    active={activeTab === index}
                                    disabled={isBlockEditMode && index === 1}
                                    onClick={() => handleTabClick(index)}
                                >
                                    {tab.label}
                                </TabButton>
                            ))}
                        </TabHeader>

                        <TabContent>
                            {availableTabs[activeTab].content}

                            <TabRight>
                                <TabBody2>Listing</TabBody2>
                                <ListingInfo>
                                    <ListingInfoWrap>
                                        <ListingInfoTitle>
                                            <TabBody2>{formData.listing.name}</TabBody2>
                                            <TabBody3>{formData.listing.kozyName}</TabBody3>
                                        </ListingInfoTitle>
                                        <ListingInfoImg
                                            src={formData.listing.image}
                                            alt={formData.listing.name}
                                        />
                                    </ListingInfoWrap>
                                    <ListingInfoWrap>
                                        <ListingInfoWrap2>
                                            <ListingInfoTag>{formData.listing.bedrooms} Beds</ListingInfoTag>
                                            <ListingInfoTag>{formData.listing.bathrooms} Baths</ListingInfoTag>
                                            <ListingInfoTag>{formData.listing.guests} Guests</ListingInfoTag>
                                        </ListingInfoWrap2>
                                    </ListingInfoWrap>
                                </ListingInfo>
                                {/* <HolidayNote>
                                Please Note: Lorem Ipsum is simply dummy text of the printing and typesetting industry.
                            </HolidayNote> */}
                            </TabRight>
                        </TabContent>

                        <ButtonWrap>
                            {availableTabs[activeTab]?.label === "Block Date" && (
                                <PopupButton
                                    onClick={isLoading ? null : handleBlockSave}
                                    disabled={isLoading}
                                    style={{
                                        opacity: isLoading ? 0.5 : 1,
                                        cursor: isLoading ? 'not-allowed' : 'pointer',
                                        backgroundColor: isLoading ? '#ccc' : ''
                                    }}
                                >
                                    {isLoading ? 'Saving...' : (data?.mode === "block-edit" ? "Save Edit" : "Save")}
                                </PopupButton>
                            )}
                            {availableTabs[activeTab]?.label === "Edit Price" && (
                                <PopupButton
                                    onClick={isLoading ? null : handlePriceSave}
                                    disabled={isLoading}
                                    style={{
                                        opacity: isLoading ? 0.5 : 1,
                                        cursor: isLoading ? 'not-allowed' : 'pointer',
                                        backgroundColor: isLoading ? '#ccc' : ''
                                    }}
                                >
                                    {isLoading ? 'Saving...' : 'Save'}
                                </PopupButton>
                            )}
                            <PopupButton
                                onClick={isLoading ? null : handleCancel}
                                disabled={isLoading}
                                style={{
                                    opacity: isLoading ? 0.5 : 1,
                                    cursor: isLoading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Cancel
                            </PopupButton>
                        </ButtonWrap>
                    </TabContainer>
                </PopupContainer>
            </PopupOverlay>
        </>);
};

export default PopUp;